import { formatCurrency } from '../../utils/money'
import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Navbar from '../Navbar';
import Modal from '../Modal';
import { fulfillmentsApi, rentalsApi } from '../../lib/api';
import ShipmentTrackingCard, { CARRIER_OPTIONS, type ShipmentTrackingData } from './ShipmentTrackingCard';
import SetupShippingModal from './SetupShippingModal';
import { assetUrl } from '../../utils/assetUrl'

interface ShippingFulfillmentDetails {
  id: string;
  status: string;
  fulfillmentType: string;
  deliveryDetails?: {
    deliveryAddress: string;
    deliveryWindowStart: string;
    deliveryWindowEnd: string;
    deliveryInstructions?: string;
    outboundShippedAt?: string | null;
  };
  rental?: {
    id: string;
    isPaid: boolean;
    shippingLabelTransactionId?: string | null;
    shippingLabelUrl?: string | null;
    shippingLabelCost?: number | null;
    shippingLabelCarrier?: string | null;
    shippingLabelServiceLevel?: string | null;
    shippingLabelFailureReason?: string | null;
    shippingLabelRefundId?: string | null;
    shippingLabelRefundStatus?: string | null;
    item: {
      title: string;
      photos?: { url: string }[];
    };
    owner: { id: string; firstName: string; lastName: string };
    renter: { id: string; firstName: string; lastName: string };
  };
}

// Counterpart to PickupTracker.tsx for Shipping/DoorDash fulfillments -- no GPS
// geofence or QR handshake, just: owner confirms a delivery window, renter later
// confirms receipt. See FULFILLMENT_PLAN.md §18.
export default function ShippingTracker() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [fulfillment, setFulfillment] = useState<ShippingFulfillmentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const [shipDate, setShipDate] = useState(tomorrow);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [instructions, setInstructions] = useState('');
  const [carrier, setCarrier] = useState(CARRIER_OPTIONS[0].value);
  const [trackingNumber, setTrackingNumber] = useState('');

  const [outboundTracking, setOutboundTracking] = useState<ShipmentTrackingData | null>(null);
  const [returnTracking, setReturnTracking] = useState<ShipmentTrackingData | null>(null);
  const [refreshingLeg, setRefreshingLeg] = useState<'outbound' | 'return' | null>(null);

  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showCancelLabelModal, setShowCancelLabelModal] = useState(false);
  const [cancellingLabel, setCancellingLabel] = useState(false);
  const [cancelLabelError, setCancelLabelError] = useState<string | null>(null);
  const [markingShipped, setMarkingShipped] = useState(false);
  const [markShippedError, setMarkShippedError] = useState<string | null>(null);

  // Whichever card holds the single actual next action (Setup Shipping / Mark
  // as Shipped / I've received the item) attaches this ref -- landing here
  // (e.g. from the post-payment notification) scrolls straight to it instead
  // of just the top of the page, so there's nothing left to hunt for. Only
  // fires once per page load, not on every refetch after an action succeeds.
  const nextActionRef = useRef<HTMLDivElement>(null);
  const hasScrolledToActionRef = useRef(false);

  const fetchFulfillment = async () => {
    if (!id) return;
    try {
      const res = await fulfillmentsApi.get(id);
      if (res.data.success) {
        setFulfillment(res.data.data);
      } else {
        setError('Fulfillment not found');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load shipment details');
    } finally {
      setLoading(false);
    }
  };

  const fetchTracking = async () => {
    if (!id) return;
    try {
      const res = await fulfillmentsApi.getTracking(id);
      if (res.data.success) {
        setOutboundTracking(res.data.data.outbound);
        setReturnTracking(res.data.data.return);
      }
    } catch {
      // Tracking is a bonus view on top of the fulfillment itself -- don't block the page on it.
    }
  };

  const handleRefreshTracking = async (leg: 'outbound' | 'return') => {
    if (!id) return;
    setRefreshingLeg(leg);
    try {
      await fulfillmentsApi.refreshTracking(id, leg);
      await fetchTracking();
    } finally {
      setRefreshingLeg(null);
    }
  };

  useEffect(() => {
    fetchFulfillment();
    fetchTracking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!loading && fulfillment && !hasScrolledToActionRef.current && nextActionRef.current) {
      nextActionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      hasScrolledToActionRef.current = true;
    }
  }, [loading, fulfillment]);

  const handleConfirmShipment = async () => {
    if (!id) return;
    setSubmitting(true);
    setActionError(null);
    try {
      const deliveryWindowStart = new Date(`${shipDate}T${startTime}`);
      const deliveryWindowEnd = new Date(`${shipDate}T${endTime}`);
      await fulfillmentsApi.confirmPickup(id, {
        pickupWindowStart: deliveryWindowStart.toISOString(),
        pickupWindowEnd: deliveryWindowEnd.toISOString(),
        instructions,
        carrier,
        trackingNumber: trackingNumber.trim() || undefined,
      });
      await fetchFulfillment();
      await fetchTracking();
    } catch (err: any) {
      setActionError(err?.response?.data?.error || 'Failed to confirm shipment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkShipped = async () => {
    if (!id) return;
    setMarkingShipped(true);
    setMarkShippedError(null);
    try {
      await fulfillmentsApi.markShipped(id);
      await fetchFulfillment();
    } catch (err: any) {
      setMarkShippedError(err?.response?.data?.error || 'Failed to notify the renter');
    } finally {
      setMarkingShipped(false);
    }
  };

  const handleLabelPurchased = async () => {
    setShowSetupModal(false);
    await fetchFulfillment();
    await fetchTracking();
  };

  const handleCancelLabel = async () => {
    if (!fulfillment?.rental?.id) return;
    setCancellingLabel(true);
    setCancelLabelError(null);
    try {
      await rentalsApi.refundShippingLabel(fulfillment.rental.id);
      setShowCancelLabelModal(false);
      await fetchFulfillment();
    } catch (err: any) {
      setCancelLabelError(err?.response?.data?.error || 'Failed to cancel shipping label');
    } finally {
      setCancellingLabel(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[var(--text-secondary)]">Loading shipment details...</div>
      </div>
    );
  }

  if (error || !fulfillment) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600">{error || 'Shipment not found'}</div>
      </div>
    );
  }

  const isOwner = user?.id === fulfillment.rental?.owner?.id;
  const isPending = fulfillment.status === 'PENDING';
  const isConfirmed = fulfillment.status === 'CONFIRMED';
  const isDeliveredOrLater = ['DELIVERED', 'RETURNED', 'COMPLETED'].includes(fulfillment.status);
  const isPaid = !!fulfillment.rental?.isPaid;
  const details = fulfillment.deliveryDetails;
  const hasLabel = !!fulfillment.rental?.shippingLabelTransactionId;
  const refundStatus = fulfillment.rental?.shippingLabelRefundStatus;
  const labelAlreadyShipped = !!outboundTracking && !['PRE_TRANSIT', 'UNKNOWN'].includes(outboundTracking.status);

  const formatWindow = (start?: string, end?: string) => {
    if (!start) return null;
    const startDate = new Date(start);
    return (
      <>
        {startDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
        {' · '}
        {startDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
        {end && <>{' – '}{new Date(end).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</>}
      </>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
      <Navbar />
      <main className="mt-[--navbar-height] min-h-[calc(100vh-var(--navbar-height))] py-6">
        <div className="container mx-auto px-4 max-w-2xl">
          <div className="mb-4">
            <Link
              to={`/rental/${fulfillment.rental?.id}`}
              className="text-sm text-[var(--text-secondary)] hover:text-[var(--forest-green)]"
            >
              ← Back to rental
            </Link>
          </div>

          {/* Header */}
          <div className="bg-white rounded-xl border border-[var(--border)] p-6 mb-4">
            <div className="flex items-center gap-4">
              {fulfillment.rental?.item.photos?.[0] ? (
                <img
                  src={assetUrl(fulfillment.rental.item.photos[0].url)}
                  alt={fulfillment.rental.item.title}
                  className="w-16 h-16 rounded-lg object-cover"
                />
              ) : (
                <div className="w-16 h-16 bg-[var(--bg-tint)] rounded-lg flex items-center justify-center">
                  <i className="mdi mdi-package-variant text-2xl text-[var(--forest-green)]"></i>
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold text-[var(--text-primary)]">
                  {fulfillment.rental?.item.title}
                </h1>
                <p className="text-sm text-[var(--text-secondary)]">Shipping fulfillment</p>
              </div>
            </div>
          </div>

          {actionError && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mb-4 text-sm">
              {actionError}
            </div>
          )}

          {!isPaid && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <p className="text-amber-800 text-sm">Waiting for the renter to pay before this can be confirmed.</p>
            </div>
          )}

          {isPaid && isPending && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <p className="text-amber-800 text-sm flex items-center gap-2">
                <i className="mdi mdi-clock-outline"></i>
                {isOwner
                  ? 'Confirm a shipping window to let the renter know when to expect it.'
                  : "Waiting for the owner to confirm a shipping window. You'll be notified once it ships."}
              </p>
            </div>
          )}

          {details?.deliveryWindowStart && !isPending && (
            <div className="bg-white rounded-xl border border-[var(--border)] p-4 mb-4">
              <div className="flex items-center gap-3">
                <i className="mdi mdi-calendar-clock text-[var(--forest-green)] text-xl"></i>
                <div>
                  <p className="text-xs text-[var(--text-muted)] font-medium uppercase tracking-wide">Shipping Window</p>
                  <p className="text-sm font-semibold text-[var(--text-primary)]">
                    {formatWindow(details.deliveryWindowStart, details.deliveryWindowEnd)}
                  </p>
                </div>
              </div>
              {details.deliveryInstructions && (
                <p className="text-sm text-[var(--text-secondary)] mt-3 pl-8">{details.deliveryInstructions}</p>
              )}
            </div>
          )}

          {details?.deliveryAddress && (
            <div className="bg-white rounded-xl border border-[var(--border)] p-4 mb-4">
              <p className="text-xs text-[var(--text-muted)] font-medium uppercase tracking-wide mb-1">Shipping To</p>
              <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                <i className="mdi mdi-map-marker text-[var(--forest-green)]"></i>
                <span>{details.deliveryAddress}</span>
              </div>
            </div>
          )}

          {outboundTracking && (
            <ShipmentTrackingCard
              title="Outbound shipment"
              tracking={outboundTracking}
              onRefresh={() => handleRefreshTracking('outbound')}
              refreshing={refreshingLeg === 'outbound'}
            />
          )}

          {returnTracking && (
            <ShipmentTrackingCard
              title="Return shipment"
              tracking={returnTracking}
              onRefresh={() => handleRefreshTracking('return')}
              refreshing={refreshingLeg === 'return'}
            />
          )}

          {/* Purchased label summary -- owner only. Cost/carrier/label-download/
              cancel are all the owner's business (what they paid Shippo, and the
              actual PDF); the renter's equivalent info (tracking number, carrier)
              already lives in the "Outbound shipment" ShipmentTrackingCard above. */}
          {hasLabel && isOwner && (
            <div className="bg-white rounded-xl border border-[var(--border)] p-4 mb-4">
              <h2 className="font-medium text-[var(--text-primary)] mb-2">Shipping label purchased</h2>
              {fulfillment.rental?.shippingLabelTransactionId?.startsWith('SIMULATED-') && (
                <div className="text-xs font-medium text-amber-700 mb-2">(Simulated — no real label was purchased)</div>
              )}
              <div className="text-sm text-[var(--text-secondary)] space-y-1">
                {fulfillment.rental?.shippingLabelCarrier && (
                  <div>
                    Carrier: {fulfillment.rental.shippingLabelCarrier}
                    {fulfillment.rental.shippingLabelServiceLevel ? ` — ${fulfillment.rental.shippingLabelServiceLevel}` : ''}
                  </div>
                )}
                {fulfillment.rental?.shippingLabelCost != null && (
                  <div>Cost: {formatCurrency(fulfillment.rental.shippingLabelCost)}</div>
                )}
              </div>
              {!details?.outboundShippedAt && (
                <div ref={nextActionRef} className="mt-3 pt-3 border-t border-[var(--border)]">
                  {markShippedError && (
                    <p className="text-xs text-red-700 mb-2">{markShippedError}</p>
                  )}
                  <button
                    onClick={handleMarkShipped}
                    disabled={markingShipped}
                    className="btn btn-accent btn-sm w-full"
                  >
                    <i className="mdi mdi-truck-check mr-1"></i>
                    {markingShipped ? 'Notifying renter...' : "I've shipped it — notify the renter"}
                  </button>
                  <p className="text-xs text-[var(--text-secondary)] mt-2">
                    Buying the label doesn't notify the renter — click this once you've actually handed the package to the carrier.
                  </p>
                </div>
              )}
              {details?.outboundShippedAt && (
                <p className="text-xs text-[var(--forest-green)] mt-3 pt-3 border-t border-[var(--border)] flex items-center gap-1">
                  <i className="mdi mdi-check-circle"></i>
                  Renter notified — shipped {new Date(details.outboundShippedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </p>
              )}
              <div className="flex flex-wrap gap-2 mt-3">
                {fulfillment.rental?.shippingLabelUrl && (
                  <a
                    href={fulfillment.rental.shippingLabelUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-secondary btn-sm"
                  >
                    <i className="mdi mdi-file-pdf-box mr-1"></i>
                    Download label
                  </a>
                )}
                {refundStatus === 'QUEUED' || refundStatus === 'PENDING' ? (
                  <p className="text-xs text-amber-700 self-center">Refund requested — pending carrier confirmation.</p>
                ) : refundStatus === 'ERROR' ? (
                  <p className="text-xs text-red-700 self-center">Refund could not be processed — the label may already be in use.</p>
                ) : (
                  <button
                    onClick={() => {
                      setCancelLabelError(null);
                      setShowCancelLabelModal(true);
                    }}
                    disabled={labelAlreadyShipped}
                    className="btn btn-danger btn-sm"
                    title={labelAlreadyShipped ? 'This label has already shipped and can no longer be cancelled' : undefined}
                  >
                    <i className="mdi mdi-cancel mr-1"></i>
                    Cancel shipping label
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Owner: buy a real label (primary path), or fall back to manual entry */}
          {isPaid && isPending && isOwner && !hasLabel && (
            <div ref={nextActionRef} className="bg-white rounded-xl border border-[var(--border)] p-4 mb-4">
              <button onClick={() => setShowSetupModal(true)} className="btn btn-accent w-full">
                <i className="mdi mdi-truck-fast mr-1"></i>
                Setup Shipping
              </button>
              <button
                onClick={() => setShowManualEntry((v) => !v)}
                className="w-full text-center text-xs text-[var(--text-secondary)] underline mt-3"
              >
                {showManualEntry ? 'Hide manual entry' : 'I already have my own tracking number'}
              </button>

              {showManualEntry && (
                <div className="space-y-4 mt-4 pt-4 border-t border-[var(--border)]">
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Ship date</label>
                    <input
                      type="date"
                      value={shipDate}
                      onChange={(e) => setShipDate(e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--forest-green)] focus:border-[var(--forest-green)]"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Window start</label>
                      <input
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="w-full px-3 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--forest-green)] focus:border-[var(--forest-green)]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Window end</label>
                      <input
                        type="time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        className="w-full px-3 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--forest-green)] focus:border-[var(--forest-green)]"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Carrier</label>
                      <select
                        value={carrier}
                        onChange={(e) => setCarrier(e.target.value)}
                        className="w-full px-3 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--forest-green)] focus:border-[var(--forest-green)]"
                      >
                        {CARRIER_OPTIONS.map((c) => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Tracking number (optional)</label>
                      <input
                        type="text"
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                        placeholder="e.g. 1Z999AA10123456784"
                        maxLength={100}
                        className="w-full px-3 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--forest-green)] focus:border-[var(--forest-green)]"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Instructions (optional)</label>
                    <textarea
                      value={instructions}
                      onChange={(e) => setInstructions(e.target.value)}
                      placeholder="e.g., leave on the porch"
                      rows={3}
                      className="w-full px-3 py-2 border border-[var(--border)] rounded-lg resize-none focus:ring-2 focus:ring-[var(--forest-green)] focus:border-[var(--forest-green)]"
                    />
                  </div>
                  <button
                    onClick={handleConfirmShipment}
                    disabled={submitting}
                    className="btn btn-primary w-full"
                  >
                    {submitting ? 'Confirming...' : 'Confirm shipment'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Renter: confirm receipt once the owner has confirmed. Routes through
              the same photo-documented inspection flow the in-person pickup path
              already uses, instead of a single click that instantly finalized
              DELIVERED with no chance to document condition or upload photos --
              a real gap found live: a renter had no way to record what shape the
              item arrived in, and no confirmation step before it was final. */}
          {isConfirmed && !isOwner && (
            <div ref={nextActionRef} className="bg-white rounded-xl border border-[var(--border)] p-4 mb-4">
              <button
                onClick={() => navigate(`/fulfillment/${id}/inspect`)}
                className="btn btn-primary w-full"
              >
                I've received the item
              </button>
            </div>
          )}

          {isDeliveredOrLater && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
              <p className="text-green-800 text-sm flex items-center gap-2">
                <i className="mdi mdi-check-circle"></i>
                {fulfillment.status === 'COMPLETED'
                  ? 'This rental is complete.'
                  : fulfillment.status === 'RETURNED'
                  ? (isOwner
                      ? "The renter has shipped the item back — check the return tracking above, then confirm receipt from the rental page."
                      : "You've shipped the item back. The owner will confirm receipt to complete the rental.")
                  : (isOwner
                      ? "The renter has confirmed receipt. It's on them until they return it."
                      : "You've confirmed receipt. Return it from the rental page when you're done.")}
              </p>
            </div>
          )}

          <button
            onClick={() => navigate(`/rental/${fulfillment.rental?.id}`)}
            className="btn btn-secondary w-full"
          >
            View rental details
          </button>
        </div>
      </main>

      {fulfillment.rental?.id && (
        <SetupShippingModal
          isOpen={showSetupModal}
          rentalId={fulfillment.rental.id}
          onClose={() => setShowSetupModal(false)}
          onPurchased={handleLabelPurchased}
        />
      )}

      <Modal
        isOpen={showCancelLabelModal}
        title="Cancel shipping label?"
        confirmLabel={cancellingLabel ? 'Requesting...' : 'Cancel label'}
        cancelLabel="Never mind"
        onConfirm={handleCancelLabel}
        onCancel={() => {
          if (!cancellingLabel) setShowCancelLabelModal(false);
        }}
        confirmVariant="danger"
        disabled={cancellingLabel}
      >
        <div>
          {cancelLabelError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm mb-3">
              {cancelLabelError}
            </div>
          )}
          <p className="text-sm">
            This requests a refund from the carrier for the unused label. It isn't instant — the carrier has to
            confirm the label was never used before the refund is approved.
          </p>
        </div>
      </Modal>
    </div>
  );
}
